import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  KeyRound,
  User,
  Briefcase,
  Heart,
  MapPin,
  CalendarDays,
  Clock,
} from 'lucide-react';
import {
  createStaffSchema,
  ROLES,
  ALL_ROLE_CODES,
  STAFF_TYPES,
  STAFF_TYPE_LABELS,
  type ApiError,
  type CreateStaffInput,
} from '@pms/shared';
import { usersService } from '@/services/users.service';
import { departmentsService } from '@/services/departments.service';
import { designationsService } from '@/services/designations.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  TEAM_LEADER: 'Team Leader',
  USER: 'User',
};

function SectionHeader({
  icon,
  title,
  optional,
}: {
  icon: React.ReactNode;
  title: string;
  optional?: boolean;
}) {
  return (
    <CardHeader className="border-b border-border/40 pb-3">
      <CardTitle className="flex items-center gap-2 text-base font-semibold text-primary">
        {icon}
        {title}
        {optional && (
          <span className="ml-auto text-xs font-normal text-muted-foreground">Optional</span>
        )}
      </CardTitle>
    </CardHeader>
  );
}

export function StaffNewPage() {
  const navigate = useNavigate();

  const { register, handleSubmit, formState, control } = useForm<CreateStaffInput>({
    resolver: zodResolver(createStaffSchema),
    defaultValues: {
      role_codes: [ROLES.USER],
      staff_type: 'permanent',
      physically_challenged: false,
      work_start_time: '09:00',
      work_end_time: '19:00',
    },
  });

  const { data: depts } = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });
  const { data: desigs } = useQuery({
    queryKey: ['designations', 'all'],
    queryFn: () => designationsService.list({ limit: 100 }),
  });

  const create = useMutation({
    mutationFn: (input: CreateStaffInput) => usersService.create(input),
    onSuccess: (res) => {
      toast.success('Staff created. Default modules assigned per role.');
      navigate(`/hrms/staff/${res.id}?tab=access`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  function onSubmit(v: CreateStaffInput) {
    create.mutate({
      ...v,
      physically_challenged: v.physically_challenged ?? false,
    });
  }

  const { errors } = formState;

  return (
    <div className="space-y-5">
      <Link
        to="/hrms/staff/list"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to staff list
      </Link>

      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">New Staff</h1>
        <p className="text-sm text-muted-foreground">
          Role default modules are auto-granted on save. Tweak per-user visibility on the Access
          tab after.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* ── Section 1: Account & Security ── */}
        <Card>
          <SectionHeader icon={<KeyRound className="h-4 w-4" />} title="Account & Security" />
          <CardContent className="grid gap-3 sm:gap-4 pt-3 sm:pt-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="employee_code">Employee Code</Label>
              <Input id="employee_code" placeholder="e.g. EMP001" {...register('employee_code')} />
              {errors.employee_code && (
                <p className="text-xs text-destructive">{errors.employee_code.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="john@example.com" {...register('email')} />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Initial Password</Label>
              <Input id="password" type="password" placeholder="Min. 8 characters" {...register('password')} />
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ── Section 2: Name & Contact ── */}
        <Card>
          <SectionHeader icon={<User className="h-4 w-4" />} title="Name & Contact" />
          <CardContent className="grid gap-3 sm:gap-4 pt-3 sm:pt-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="first_name">First Name</Label>
              <Input id="first_name" placeholder="John" {...register('first_name')} />
              {errors.first_name && (
                <p className="text-xs text-destructive">{errors.first_name.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last_name">Last Name</Label>
              <Input id="last_name" placeholder="Doe" {...register('last_name')} />
              {errors.last_name && (
                <p className="text-xs text-destructive">{errors.last_name.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" placeholder="1234567890" {...register('phone')} />
              {errors.phone && (
                <p className="text-xs text-destructive">{errors.phone.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gender">Gender</Label>
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger id="gender"><SelectValue placeholder="— Select —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.gender && (
                <p className="text-xs text-destructive">{errors.gender.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ── Section 3: Organization ── */}
        <Card>
          <SectionHeader icon={<Briefcase className="h-4 w-4" />} title="Organization" />
          <CardContent className="grid gap-3 sm:gap-4 pt-3 sm:pt-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="department_id">Department</Label>
              <Controller
                name="department_id"
                control={control}
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger id="department_id"><SelectValue placeholder="— Select —" /></SelectTrigger>
                    <SelectContent>
                      {(depts?.data ?? []).map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.department_id && (
                <p className="text-xs text-destructive">{errors.department_id.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="designation_id">Designation</Label>
              <Controller
                name="designation_id"
                control={control}
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger id="designation_id"><SelectValue placeholder="— Select —" /></SelectTrigger>
                    <SelectContent>
                      {(desigs?.data ?? []).map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.designation_id && (
                <p className="text-xs text-destructive">{errors.designation_id.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="staff_type">Staff Type</Label>
              <Controller
                name="staff_type"
                control={control}
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger id="staff_type"><SelectValue placeholder="— Select —" /></SelectTrigger>
                    <SelectContent>
                      {STAFF_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>{STAFF_TYPE_LABELS[t]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.staff_type && (
                <p className="text-xs text-destructive">{errors.staff_type.message}</p>
              )}
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Role(s)</Label>
              <div className="flex flex-wrap gap-3 sm:gap-4">
                <Controller
                  name="role_codes"
                  control={control}
                  render={({ field }) => (
                    <>
                      {ALL_ROLE_CODES.map((code) => (
                        <label
                          key={code}
                          className="flex cursor-pointer items-center gap-2 text-sm"
                        >
                          <Checkbox
                            checked={field.value?.includes(code) ?? false}
                            onChange={(e) => {
                              const current = field.value ?? [];
                              field.onChange(
                                e.target.checked
                                  ? [...current, code]
                                  : current.filter((c) => c !== code),
                              );
                            }}
                          />
                          {ROLE_LABELS[code] ?? code}
                        </label>
                      ))}
                    </>
                  )}
                />
              </div>
              {errors.role_codes && (
                <p className="text-xs text-destructive">{errors.role_codes.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Role default modules are auto-granted. Adjust on the Access tab after creation.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* ── Section 4: Personal Details ── */}
        <Card>
          <SectionHeader
            icon={<User className="h-4 w-4" />}
            title="Personal Details"
          />
          <CardContent className="grid gap-3 sm:gap-4 pt-3 sm:pt-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="dob">Date of Birth</Label>
              <Controller
                control={control}
                name="dob"
                render={({ field }) => (
                  <DatePicker id="dob" value={field.value ?? ''} onChange={(v) => field.onChange(v ?? '')} />
                )}
              />
              {errors.dob && (
                <p className="text-xs text-destructive">{errors.dob.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="blood_group">Blood Group</Label>
              <Controller
                name="blood_group"
                control={control}
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger id="blood_group"><SelectValue placeholder="— Select —" /></SelectTrigger>
                    <SelectContent>
                      {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((bg) => (
                        <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="marital_status">Marital Status</Label>
              <Controller
                name="marital_status"
                control={control}
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger id="marital_status"><SelectValue placeholder="— Select —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                      <SelectItem value="divorced">Divorced</SelectItem>
                      <SelectItem value="widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="emergency_contact">Emergency Contact</Label>
              <Input id="emergency_contact" placeholder="Phone number" {...register('emergency_contact')} />
            </div>
            <div className="flex items-center gap-2 pt-1 md:col-span-2">
              <Controller
                name="physically_challenged"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="physically_challenged"
                    checked={field.value ?? false}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
              <Label htmlFor="physically_challenged" className="cursor-pointer font-normal">
                Physically Challenged
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* ── Section 5: Family Details (Optional) ── */}
        <Card>
          <SectionHeader icon={<Heart className="h-4 w-4" />} title="Family Details" optional />
          <CardContent className="grid gap-3 sm:gap-4 pt-3 sm:pt-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="father_name">Father's Name</Label>
              <Input id="father_name" {...register('father_name')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mother_name">Mother's Name</Label>
              <Input id="mother_name" {...register('mother_name')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="spouse_name">Spouse Name</Label>
              <Input id="spouse_name" {...register('spouse_name')} />
            </div>
          </CardContent>
        </Card>

        {/* ── Section 6: Address & Employment ── */}
        <Card>
          <SectionHeader icon={<MapPin className="h-4 w-4" />} title="Address & Employment" />
          <CardContent className="grid gap-3 sm:gap-4 pt-3 sm:pt-5 md:grid-cols-2">
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="address_line1">Address Line 1</Label>
              <Input id="address_line1" {...register('address_line1')} />
              {errors.address_line1 && (
                <p className="text-xs text-destructive">{errors.address_line1.message}</p>
              )}
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="address_line2">Address Line 2</Label>
              <Input id="address_line2" {...register('address_line2')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="city">City</Label>
              <Input id="city" {...register('city')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="state">State</Label>
              <Input id="state" {...register('state')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="postal_code">Postal Code</Label>
              <Input id="postal_code" {...register('postal_code')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="joining_date">
                <CalendarDays className="mr-1 inline h-3.5 w-3.5" />
                Joining Date
              </Label>
              <Controller
                control={control}
                name="joining_date"
                render={({ field }) => (
                  <DatePicker id="joining_date" value={field.value ?? ''} onChange={(v) => field.onChange(v ?? '')} />
                )}
              />
              {errors.joining_date && (
                <p className="text-xs text-destructive">{errors.joining_date.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ── Section 7: Working Hours ── */}
        <Card>
          <SectionHeader icon={<Clock className="h-4 w-4" />} title="Working Hours" />
          <CardContent className="grid gap-3 sm:gap-4 pt-3 sm:pt-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Shift Start Time <span className="text-destructive">*</span></Label>
              <Controller
                control={control}
                name="work_start_time"
                render={({ field }) => (
                  <TimePicker value={field.value ?? ''} onChange={field.onChange} />
                )}
              />
              {formState.errors.work_start_time && (
                <p className="text-xs text-destructive">{formState.errors.work_start_time.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Shift End Time <span className="text-destructive">*</span></Label>
              <Controller
                control={control}
                name="work_end_time"
                render={({ field }) => (
                  <TimePicker value={field.value ?? ''} onChange={field.onChange} />
                )}
              />
              {formState.errors.work_end_time && (
                <p className="text-xs text-destructive">{formState.errors.work_end_time.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pb-2">
          <Button type="button" variant="outline" onClick={() => navigate('/hrms/staff/list')}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create Staff'}
          </Button>
        </div>
      </form>
    </div>
  );
}
